import React, { useState, useEffect, useMemo } from 'react';
import './Alerts.css';
import ContentWrapper from './ContentWrapper';
import UniformTable from './UniformTable';
import { toast } from 'react-toastify';
import FilterDropdown from './FilterDropdown';

const ALERT_TYPES = {
  RT: 'Rash Turn',
  BD: 'Battery Disconnect',
  HA: 'Harsh Acceleration',
  HB: 'Harsh Braking',
  BL: 'Battery Low'
};

const ALERT_TYPE_ORDER = ['RT', 'BD', 'HA', 'HB', 'BL'];

const ALERT_COLORS = {
  RT: '#4c1d95',
  BD: '#1d4ed8',
  HA: '#0f766e',
  HB: '#22c55e',
  BL: '#eab308'
};

const toUtcStamp = dt => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mm = String(dt.getUTCMinutes()).padStart(2, '0');
  const ss = String(dt.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${d}${hh}${mm}${ss}`;
};

const formatIstTime = value => {
  if (!value) return '-';
  const s = String(value);
  let date;
  if (/^\d{14}$/.test(s)) {
    const y = Number(s.slice(0, 4));
    const m = Number(s.slice(4, 6)) - 1;
    const d = Number(s.slice(6, 8));
    const hh = Number(s.slice(8, 10));
    const mm = Number(s.slice(10, 12));
    const ss = Number(s.slice(12, 14));
    date = new Date(Date.UTC(y, m, d, hh, mm, ss));
  } else {
    date = new Date(s);
  }
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const buildIstRange = (start, end) => {
  if (!start || !end) return '';
  if (
    Number.isNaN(start.getTime ? start.getTime() : NaN) ||
    Number.isNaN(end.getTime ? end.getTime() : NaN)
  ) {
    return '';
  }
  const dateOpts = {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  };
  const timeOpts = {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  const startDate = start.toLocaleDateString('en-IN', dateOpts);
  const endDate = end.toLocaleDateString('en-IN', dateOpts);
  const startTime = start.toLocaleTimeString('en-IN', timeOpts);
  const endTime = end.toLocaleTimeString('en-IN', timeOpts);
  if (startDate === endDate) {
    return `${startDate} | ${startTime} – ${endTime}`;
  }
  return `${startDate} ${startTime} – ${endDate} ${endTime}`;
};

const rangeLabelForPreset = preset => {
  if (preset === '4h') return 'Last 4 Hours';
  if (preset === '12h') return 'Last 12 Hours';
  if (preset === '24h') return 'Last 24 Hours';
  if (preset === '7d') return 'Last 7 Days';
  return 'Custom Range';
};

const buildPresetRange = preset => {
  const now = new Date();
  let durationMs = 4 * 60 * 60 * 1000;
  if (preset === '12h') durationMs = 12 * 60 * 60 * 1000;
  if (preset === '24h') durationMs = 24 * 60 * 60 * 1000;
  if (preset === '7d') durationMs = 7 * 24 * 60 * 60 * 1000;
  const start = new Date(now.getTime() - durationMs);
  return { start, end: now, label: rangeLabelForPreset(preset) };
};

export default function Alerts({
  devices = [],
  onFullWidthChange,
  setBackAction,
  setPageTitle
}) {
  const [selectedImei, setSelectedImei] = useState(null);
  const [query, setQuery] = useState('');
  const [timePreset, setTimePreset] = useState('4h');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [activeRange, setActiveRange] = useState(() => buildPresetRange('4h'));
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [showCustomPopover, setShowCustomPopover] = useState(false);

  const apiKey = useMemo(
    () =>
      process.env.REACT_APP_API_KEY ||
      (typeof window !== 'undefined' && window.REACT_APP_API_KEY) ||
      '',
    []
  );
  const googleKey = useMemo(
    () => process.env.REACT_APP_GOOGLE_MAPS_KEY || '',
    []
  );

  useEffect(() => {
    if (onFullWidthChange) onFullWidthChange(Boolean(selectedImei));
  }, [selectedImei, onFullWidthChange]);

 useEffect(() => {
  if (selectedImei && setBackAction) {
    setBackAction(() => () => window.history.back());
  }
  return () => {
    if (setBackAction) setBackAction(null);
  };
}, [selectedImei, setBackAction]);
  useEffect(() => {
  const handlePopState = () => {
    const match = window.location.pathname.match(/^\/alerts\/(.+)$/);
    setSelectedImei(match ? match[1] : null);
  };
  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}, []);

  useEffect(() => {
    if (timePreset !== 'custom') {
      setActiveRange(buildPresetRange(timePreset));
    }
  }, [timePreset]);

  const handleApplyCustomRange = () => {
    if (!customStart || !customEnd) {
      toast.warning('Select both start and end time for custom range');
      return false;
    }
    const start = new Date(customStart);
    const end = new Date(customEnd);
    if (!start.getTime() || !end.getTime() || start >= end) {
      toast.error('Custom range start must be before end');
      return;
    }
    setActiveRange({ start, end, label: 'Custom Range' });
    return true;
  };

  useEffect(() => {
    if (!selectedImei) {
      setAlerts([]);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const fromStamp = toUtcStamp(activeRange.start);
        const toStamp = toUtcStamp(activeRange.end);
        const imeiParam = encodeURIComponent(selectedImei);
        const url = `${process.env.REACT_APP_VEHICLE_ALERT_API_BASE}/alerts?imeis=${imeiParam}&TimeRange=${fromStamp},${toStamp}`


        const res = await fetch(url, {
          headers: apiKey ? { 'x-api-key': apiKey } : undefined
        });

        let json;
        try {
          json = await res.json();
        } catch {
          json = {};
        }

        let data = [];
        if (Array.isArray(json)) {
          data = json;
        } else if (Array.isArray(json.data)) {
          data = json.data;
        } else if (Array.isArray(json.processedAlerts)) {
          data = json.processedAlerts;
        } else if (json.body) {
          try {
            const parsed = JSON.parse(json.body);
            if (Array.isArray(parsed)) {
              data = parsed;
            } else if (Array.isArray(parsed.data)) {
              data = parsed.data;
            } else if (Array.isArray(parsed.processedAlerts)) {
              data = parsed.processedAlerts;
            }
          } catch {
            data = [];
          }
        }

        const normalized = [];
        for (const raw of data) {
          const imei = String(
            raw.imei || raw.IMEI || raw.device_imei || ''
          ).trim();
          const ts =
            raw.time_stamp || raw.timestamp || raw.alert_time || null;
          const packet = raw.packet_data || raw.packet || '';
          const lat = raw.latitude ?? raw.lat ?? null;
          const lng = raw.longitude ?? raw.lng ?? null;
          let code =
            raw.alertCode ||
            raw.alertType ||
            raw.alert_type ||
            raw.type ||
            null;

          if (!code && typeof packet === 'string') {
            const parts = packet.split(',');
            if (parts.length > 5) {
              code = parts[4];
            }
          }

          if (!code || !ALERT_TYPES[code]) {
            continue;
          }

          normalized.push({
            id:
              raw.id ||
              `${imei || 'unknown'}-${ts || ''}-${code}-${Math.random()
                .toString(36)
                .slice(2)}`,
            imei,
            alertCode: code,
            timestamp: ts,
            lat: lat != null ? Number(lat) : null,
            lng: lng != null ? Number(lng) : null,
            address: raw.address || ''
          });
        }

        normalized.sort((a, b) => {
          const at = a.timestamp ? Number(a.timestamp) || 0 : 0;
          const bt = b.timestamp ? Number(b.timestamp) || 0 : 0;
          return bt - at;
        });

        let alertsWithAddress = normalized;
        if (googleKey) {
          const limit = 50;
          const head = normalized.slice(0, limit);
          const tail = normalized.slice(limit);
          const enrichedHead = await Promise.all(
            head.map(async a => {
              if (
                a.address ||
                a.lat == null ||
                a.lng == null ||
                !Number.isFinite(a.lat) ||
                !Number.isFinite(a.lng)
              ) {
                return a;
              }
              try {
                const res = await fetch(
                  `${process.env.REACT_APP_GOOGLE_GEOCODE_API_URL || 'https://maps.googleapis.com/maps/api/geocode/json'}?latlng=${a.lat},${a.lng}&key=${googleKey}`
                );
                const geo = await res.json();
                const formatted =
                  Array.isArray(geo.results) &&
                    geo.results[0] &&
                    geo.results[0].formatted_address
                    ? geo.results[0].formatted_address
                    : '';
                return { ...a, address: formatted || a.address };
              } catch {
                return a;
              }
            })
          );
          alertsWithAddress = [...enrichedHead, ...tail];
        }

        if (!cancelled) {
          setAlerts(alertsWithAddress);
        }
      } catch (error) {
        console.error('[Alerts] Failed to load alerts', error);
        if (!cancelled) {
          toast.error('Failed to load alerts for the selected time range');
          setAlerts([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [selectedImei, activeRange, apiKey, googleKey]);

  const totalAlerts = alerts.length;

  const distribution = ALERT_TYPE_ORDER.map(code => {
    const count = alerts.filter(a => a.alertCode === code).length;
    return {
      code,
      label: ALERT_TYPES[code],
      count
    };
  });

  const maxCount = distribution.reduce(
    (acc, d) => (d.count > acc ? d.count : acc),
    0
  );

  const istRangeText =
    activeRange.start && activeRange.end
      ? buildIstRange(activeRange.start, activeRange.end)
      : '';

  if (!selectedImei) {
    const filteredDevices =
      query.trim() === ''
        ? devices
        : devices.filter(r => {
          const q = query.trim().toLowerCase();
          return (
            String(r.name || r.deviceName || '')
              .toLowerCase()
              .includes(q) ||
            String(r.imei || '')
              .toLowerCase()
              .includes(q)
          );
        });

    const columns = [
      { header: '#', accessor: 'number' },
      { header: 'Name', accessor: 'name' },
      { header: 'IMEI', accessor: 'imei' },
      { header: 'Action', accessor: 'action' }
    ];

    const rows = filteredDevices.map((d, i) => ({
      number: (
        <span className="badge fleet-table-index">
          {String(i + 1).padStart(2, '0')}
        </span>
      ),
      name: d.name || d.deviceName,
      imei: <span className="mono">{d.imei}</span>,
      action: (
        <div style={{ textAlign: 'center' }}>
          <button
            className="soft"
            onClick={() => {
  setLoading(true);
  setSelectedImei(d.imei);
  window.history.pushState({}, '', `/alerts/${d.imei}`);
}}
          >
            View Alerts
          </button>
        </div>
      )
    }));

    return (
      <ContentWrapper title="Alerts">
        <div className="trip-report devices">
          <div className="devices-desktop">
            <div className="devices-header-row">
              <input
                className="devices-search-input"
                type="text"
                placeholder="Search name, IMEI…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>

            <div className="devices-table-container fleet-table-wrapper">
              {rows.length === 0 ? (
                <div className="align-center fleet-table-empty">No devices found</div>
              ) : (
                <UniformTable columns={columns} data={rows} />
              )}
            </div>
          </div>

          <div className="devices-mobile">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                gap: 8
              }}
            >
              <h2 style={{ margin: 0, flex: 1 }}>Alerts</h2>
            </div>
            <input
              className="mobile-search"
              type="text"
              placeholder="Search…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <div className="mobile-devices-container">
              {filteredDevices.length === 0 ? (
                <div className="mobile-empty">No devices found</div>
              ) : (
                filteredDevices.map(d => (
                  <div key={d.imei} className="mobile-device-card">
                    <div className="device-card-header">
                      <h3 className="device-name">{d.name || d.deviceName}</h3>
                    </div>
                    <div className="device-card-body">
                      <div className="device-info">
                        <span className="info-label">IMEI:</span>
                        <span className="info-value mono">{d.imei}</span>
                      </div>
                    </div>
                    <div className="device-card-actions">
                      <button
                        className="mobile-action-btn primary"
                       onClick={() => {
  setLoading(true);
  setSelectedImei(d.imei);
  window.history.pushState({}, '', `/alerts/${d.imei}`);
}}
                      >
                        View Alerts
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </ContentWrapper>
    );

  }

  return (
    <div className="alerts-report">
      <div className="alerts-header-row">
        <div className="alerts-header-left">
          {selectedImei && (
            <div className="alerts-header-subtitle">
              IMEI: <span className="mono">{selectedImei}</span>
            </div>
          )}
        </div>
        <div className="alerts-header-right">
          <div className="time-picker-anchor">
           <FilterDropdown
  value={timePreset}
  onChange={(value) => {
    setTimePreset(value);
    setShowCustomPopover(value === 'custom');
  }}
  options={[
    { value: '4h', label: 'Last 4 Hours' },
    { value: '12h', label: 'Last 12 Hours' },
    { value: '24h', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: 'custom', label: 'Custom Range' }
  ]}
/>
            {timePreset === 'custom' && showCustomPopover && (
              <div className={`alerts-custom-range ${showCustomPopover ? 'is-open' : ''}`}>
                <label>
                  Start (IST)
                  <input
                    type="datetime-local"
                    value={customStart}
                    onChange={e => setCustomStart(e.target.value)}
                  />
                </label>
                <label>
                  End (IST)
                  <input
                    type="datetime-local"
                    value={customEnd}
                    onChange={e => setCustomEnd(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="apply-btn"
                  onClick={() => {
                    const applied = handleApplyCustomRange();
                    if (applied) setShowCustomPopover(false);
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner-circle" />
          <div>Loading alerts…</div>
        </div>
      ) : (
        <>
          <div className="alerts-top-row">
            <section className="alerts-summary">
              <div className="summary-card">
                <div className="summary-icon-box" aria-hidden="true">⚠️</div>
                <div className="summary-title">
                  Total Alerts ({activeRange.label})
                </div>
                <div className="summary-count">
                  {totalAlerts}
                </div>
                {istRangeText && (
                  <div className="summary-subtitle">
                    Displayed Time (IST): <span>{istRangeText}</span>
                  </div>
                )}
              </div>
            </section>

            <section className="alerts-chart-section">
              <div className="chart-header">
                <h3>Alert Distribution</h3>
                <div className="chart-subtitle">
                  {activeRange.label}
                </div>
              </div>
              {totalAlerts === 0 ? (
                <div className="no-data">
                  No alerts found for the selected time range
                </div>
              ) : (
                <>
                  <div className="alerts-chart-bars">
                    {distribution.map(item => (
                      <div
                        key={item.code}
                        className="alerts-bar-column"
                      >
                        <div className="alerts-bar-wrapper">
                          <div
                            className="alerts-bar"
                            style={{
                              height:
                                maxCount && item.count
                                  ? `${(item.count / maxCount) * 100 || 0}%`
                                  : '2px',
                              backgroundColor:
                                ALERT_COLORS[item.code] || '#6b7280'
                            }}
                          />
                        </div>
                        <div className="alerts-bar-label">
                          {item.code}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="alerts-legend-row">
                    {ALERT_TYPE_ORDER.map(code => {
                      const row = distribution.find(d => d.code === code);
                      return (
                        <div
                          key={code}
                          className="legend-dot-item"
                        >
                          <span
                            className="legend-dot"
                            style={{
                              backgroundColor:
                                ALERT_COLORS[code] || '#6b7280'
                            }}
                          />
                          <span className="legend-dot-label">
                            {ALERT_TYPES[code]} ({row ? row.count : 0})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          </div>

          <section className="alerts-table-section">
            <div className="alerts-table-header">
            </div>
            <div className="alerts-table-card">
              {totalAlerts === 0 ? (
                <div className="alerts-empty-state">
                  <div className="alerts-empty-icon">🔍</div>
                  <div className="alerts-empty-title">No Alerts Found</div>
                  <div className="alerts-empty-copy">
                    Everything looks good! There are no critical alerts or hardware notifications for the selected time period.
                  </div>
                </div>
              ) : (
                <div className="table-responsive-fleet-table-wrapper">
                  {alerts.length === 0 ? (
                    <div className="align-center fleet-table-empty">No alerts found</div>
                  ) : (
                    <>
                      <div className="alerts-table-wrapper--desktop">
                        <UniformTable
                          columns={[
                            { header: '#', accessor: 'number', width: '60px', align: 'center' },
                            { header: 'IMEI', accessor: 'imei', width: '1.5fr', align: 'left' },
                            { header: 'Alert Type', accessor: 'alertType', width: '1.4fr', align: 'left' },
                            { header: 'Timestamp (IST)', accessor: 'timestamp', width: '1.6fr', align: 'left' },
                            { header: 'Latitude', accessor: 'latitude', width: '1fr', align: 'left' },
                            { header: 'Longitude', accessor: 'longitude', width: '1fr', align: 'left' },
                            { header: 'Address', accessor: 'address', width: '2fr', align: 'left' }
                          ]}
                          data={alerts.map((a, index) => ({
                            number: index + 1,
                            imei: <span className="mono">{a.imei}</span>,
                            alertType: a.alertCode,
                            timestamp: formatIstTime(a.timestamp),
                            latitude: a.lat != null ? Number(a.lat).toFixed(5) : '-',
                            longitude: a.lng != null ? Number(a.lng).toFixed(5) : '-',
                            address: a.address || '-'
                          }))}
                        />
                      </div>

                      <div className="alerts-mobile-list">
                        {alerts.map((a, index) => (
                          <div key={`${a.id || a.imei || index}`} className="alerts-mobile-card">
                            <div className="alert-title">{a.alertCode || 'Alert'}</div>
                            <div className="alert-detail">
                              <span className="label">IMEI</span>
                              <span className="value mono">{a.imei || '-'}</span>
                            </div>
                            <div className="alert-detail">
                              <span className="label">Time</span>
                              <span className="value">{formatIstTime(a.timestamp)}</span>
                            </div>
                            <div className="alert-detail">
                              <span className="label">Latitude</span>
                              <span className="value">{a.lat != null ? Number(a.lat).toFixed(5) : '-'}</span>
                            </div>
                            <div className="alert-detail">
                              <span className="label">Longitude</span>
                              <span className="value">{a.lng != null ? Number(a.lng).toFixed(5) : '-'}</span>
                            </div>
                            <div className="alert-detail">
                              <span className="label">Address</span>
                              <span className="value">{a.address || '-'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}